import React, { useEffect, useState } from 'react';
import { MdClose, MdError, MdOpenInNew } from 'react-icons/md';

interface WebViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  title?: string;
  width?: string;
  height?: string;
}

const WebViewModal: React.FC<WebViewModalProps> = ({
  isOpen,
  onClose,
  url,
  title = 'Web Content',
  width = '90vw',
  height = '80vh',
}) => {
  const [iframeError, setIframeError] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false); // Start with false for PDFs

  // Reset modal state when URL changes
  useEffect(() => {
    if (isOpen) {
      resetModal();

      // Set a timeout to handle cases where iframe doesn't load
      const timeout = setTimeout(() => {
        setIframeLoading(false);
        setIframeError(true);
      }, 8000); // 8 second timeout for PDFs

      return () => clearTimeout(timeout);
    }
  }, [url, isOpen, iframeLoading]);

  const resetModal = () => {
    setIframeError(false);
    setIframeLoading(false); // Don't show loading for PDFs initially
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleIframeLoad = () => {
    setIframeLoading(false);
    setIframeError(false);
  };

  const handleIframeError = () => {
    setIframeLoading(false);
    setIframeError(true);
  };

  // Show loading only if PDF takes too long to appear
  const handleIframeLoadStart = () => {
    // Show loading after 1 second if PDF hasn't loaded yet
    setTimeout(() => {
      if (!iframeError) {
        setIframeLoading(true);
      }
    }, 1000);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div
        className="bg-white rounded-lg shadow-xl flex flex-col"
        style={{ width, height, maxWidth: '95vw', maxHeight: '95vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 truncate pr-4">
            {title}
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-gray-100 transition-colors"
            aria-label="Close modal"
          >
            <MdClose size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 relative">
          {iframeLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading PDF...</p>
              </div>
            </div>
          )}

          {iframeError ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <div className="text-center p-6">
                <MdError size={48} className="text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  PDF Cannot Be Displayed
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  This PDF cannot be displayed within the application.
                </p>
                <div className="space-y-2">
                  <button
                    onClick={() =>
                      window.open(url, '_blank', 'noopener,noreferrer')
                    }
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <MdOpenInNew size={16} className="mr-2" />
                    Open PDF in New Tab
                  </button>
                  <div className="text-xs text-gray-500">
                    <p>This may be due to:</p>
                    <ul className="list-disc list-inside mt-1">
                      <li>PDF viewer not available in browser</li>
                      <li>Network connectivity issues</li>
                      <li>PDF file access restrictions</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <iframe
              src={`${url}#toolbar=1&navpanes=1&scrollbar=1`}
              className="w-full h-full border-0"
              title={title}
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
              loading="eager"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              onLoadStart={handleIframeLoadStart}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600 truncate flex-1">{url}</div>
          <button
            onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
            className="ml-4 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Open in New Tab
          </button>
        </div>
      </div>
    </div>
  );
};

export default WebViewModal;
